import {
   BoltIcon,
   SknowFlakeIcon,
   Start02Icon,
   UsersIcon,
} from '@forwardflow/icons'
import { ICampaignType, IFilterCriteria } from '@forwardflow/types/onboarding'
import { ContentCard, Group, Stack, Radio, TextInput } from '@forwardflow/ui-v2'
import cn from 'classnames'
import { useState } from 'react'
import { FilterDefinition } from '.'
import { useCampaignStore } from '@forwardflow/store'

const CAMPAIGN_TYPE_OPTIONS = [
   {
      id: 'new-lead',
      label: 'New Leads',
      description:
         'Instantly engage every new inquiry the moment it enters your CRM.',
      icon: <BoltIcon width={23} height={23} color="#9F7BFF" />,
      color: '#9F7BFF',
      showBufferTime: true,
   },
   {
      id: 'revive',
      label: 'Cold Leads',
      description: 'Re-engage past prospects who went cold.',
      icon: <SknowFlakeIcon width={23} height={23} color="#1FB6FF" />,
      color: '#1FB6FF',
      showBufferTime: false,
   },
   {
      id: 'both',
      label: 'Both',
      description: 'Combine new and old for full-funnel coverage.',
      icon: <UsersIcon width={23} height={23} color="#05DF72" />,
      color: '#05DF72',
      showBufferTime: true,
   },
]

export const KindsOfLeads = () => {
   const {
      setCampaignConfiguration,
      campaignConfiguration,
      getFirstFilterCriteria,
      getRestFilterCriterias,
   } = useCampaignStore()
   const firstFilterCriteria = getFirstFilterCriteria()
   const restFilterCriterias = getRestFilterCriterias()

   const existingBufferTime =
      campaignConfiguration?.first_to_contact?.buffer_time || 0
   const [hasDelay, setHasDelay] = useState<boolean>(
      existingBufferTime > 0 ? true : false
   )
   const [delayMinutes, setDelayMinutes] = useState<string>(
      existingBufferTime > 0 ? existingBufferTime.toString() : ''
   )

   const selectedCampaignType = campaignConfiguration?.campaign_type
   const showDelayOption =
      selectedCampaignType === 'new-lead' || selectedCampaignType === 'both'
   const delayWarning = delayMinutes && parseInt(delayMinutes) > 20

   // Reset delay state when campaign type changes to one that doesn't support delay
   const handleSelectCampaignType = (optionId: ICampaignType) => {
      setCampaignConfiguration({
         campaign_type: optionId,
      })
      if (optionId === 'revive') {
         setHasDelay(false)
         setDelayMinutes('')
         // Reset buffer_time to 0 when delay is not applicable
         setCampaignConfiguration({
            first_to_contact: {
               buffer_time: 0,
            },
         })
      }
   }

   const handleDelayChange = (value: boolean) => {
      setHasDelay(value)
      if (value === false) {
         setDelayMinutes('')
         setCampaignConfiguration({
            first_to_contact: {
               buffer_time: 0,
            },
         })
      }
   }

   const handleDelayMinutesChange = (value: string) => {
      setDelayMinutes(value)
      const minutes = parseInt(value)
      if (!isNaN(minutes) && minutes > 0) {
         setCampaignConfiguration({
            first_to_contact: {
               buffer_time: minutes,
            },
         })
      }
   }

   return (
      <Stack>
         <ContentCard className="space-y-4 !p-0">
            <Stack className="p-6 border-b border-[#272E35]">
               <Group>
                  <Start02Icon />
                  <p className="text-white font-semibold text-lg">
                     Which leads should Forward Flow work on?
                  </p>
               </Group>
               <FilterDefinition
                  filterCriteria={firstFilterCriteria as IFilterCriteria}
               />
            </Stack>
            <Stack className="p-6 border-b border-[#272E35] py-4 pt-0">
               {restFilterCriterias.map((filterCriteria) => (
                  <FilterDefinition
                     key={filterCriteria.field}
                     filterCriteria={filterCriteria as IFilterCriteria}
                  />
               ))}
            </Stack>
            <Stack className="p-6 pt-0">
               <p className="text-white font-semibold text-lg">
                  Which type of leads should Forward Flow work on?
               </p>
               <Stack gap={16}>
                  {CAMPAIGN_TYPE_OPTIONS.map((option) => (
                     <button
                        key={option.id}
                        className={cn(
                           'rounded-[8px] bg-white/5 p-4 text-left flex items-center gap-4',
                           campaignConfiguration?.campaign_type === option.id &&
                              'bg-[#A684FF1A] border border-[#A684FF]/40'
                        )}
                        onClick={() =>
                           handleSelectCampaignType(option.id as ICampaignType)
                        }
                     >
                        <Group
                           className={`rounded-[8px] p-2`}
                           justify="center"
                           align="center"
                           style={{ backgroundColor: `${option.color}1A` }}
                        >
                           {option.icon}
                        </Group>
                        <Stack gap={2}>
                           <p className="text-white font-semibold text-lg">
                              {option.label}
                           </p>
                           <p className="text-white/70 text-xs">
                              {option.description}
                           </p>
                        </Stack>
                     </button>
                  ))}
               </Stack>
            </Stack>
         </ContentCard>

         {/* Delay Selection - Show only for new-lead or both */}
         {showDelayOption && (
            <ContentCard className="space-y-4 !p-6">
               <Stack gap={16}>
                  <p className="text-white font-medium text-base">
                     Would you like us to set a delay, so your agents have time
                     to call new leads before Forward Flow calls them?
                  </p>

                  <Group gap={16}>
                     <button
                        className={cn(
                           'rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2 hover:bg-white/[0.05] transition-colors',
                           hasDelay === true &&
                              'bg-[#A684FF1A] border-[#A684FF]/40'
                        )}
                        onClick={() => handleDelayChange(true)}
                     >
                        <Group gap={8}>
                           <Radio readOnly checked={hasDelay === true} />
                           <p className="text-white text-sm font-medium">Yes</p>
                        </Group>
                     </button>

                     <button
                        className={cn(
                           'rounded-lg border border-white/[0.1] bg-white/[0.02] px-4 py-2 hover:bg-white/[0.05] transition-colors',
                           hasDelay === false &&
                              'bg-[#A684FF1A] border-[#A684FF]/40'
                        )}
                        onClick={() => handleDelayChange(false)}
                     >
                        <Group gap={8}>
                           <Radio readOnly checked={hasDelay === false} />
                           <p className="text-white text-sm font-medium">No</p>
                        </Group>
                     </button>
                  </Group>

                  {hasDelay === true && (
                     <Stack gap={8}>
                        <TextInput
                           label="Delay time (minutes)"
                           description="When a new lead is generated, how long would you like FF to wait prior to engaging, giving your agents time to take action."
                           placeholder="Enter delay in minutes"
                           type="number"
                           min="1"
                           value={delayMinutes}
                           onChange={(e) =>
                              handleDelayMinutesChange(e.target.value)
                           }
                        />
                        {delayWarning && (
                           <p className="text-[#FFA500] text-sm font-medium">
                              ⚠️ This can negatively impact your ROI on leads.
                              We recommend only setting a delay if you are sure
                              your team is going to call the leads.
                           </p>
                        )}
                     </Stack>
                  )}
               </Stack>
            </ContentCard>
         )}
      </Stack>
   )
}
